import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Search, TrendingUp, RefreshCw, Calendar, BarChart3, 
    ArrowUpRight, ArrowDownRight, Clock, Users, Sparkles,
    ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminAnalyticsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [searchData, setSearchData] = useState(null);
    const [improvementData, setImprovementData] = useState(null);
    const [period, setPeriod] = useState('all');
    const [expandedSection, setExpandedSection] = useState('searches');

    const getAuthHeaders = () => {
        const token = localStorage.getItem('admin_token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
    };

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const [searchRes, improvementRes] = await Promise.all([
                fetch(`${API_URL}/api/admin/analytics/searches?period=${period}&limit=20`, {
                    headers: getAuthHeaders()
                }),
                fetch(`${API_URL}/api/admin/analytics/improvements?limit=10`, {
                    headers: getAuthHeaders()
                })
            ]);

            if (searchRes.status === 401 || improvementRes.status === 401) {
                navigate('/admin/login');
                return;
            }

            const searchJson = await searchRes.json();
            const improvementJson = await improvementRes.json();

            setSearchData(searchJson);
            setImprovementData(improvementJson);
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            toast.error('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [period]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background p-6 flex items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const stats = searchData?.stats || {};
    const topSearches = searchData?.top_searches || [];
    const improvementStats = improvementData?.stats || {};
    const recentImprovements = improvementData?.recent_improvements || [];
    const qualityReport = improvementData?.ai_quality_report || {};

    return (
        <div className="min-h-screen bg-background p-6" data-testid="admin-analytics-page">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-foreground">Analytics Dashboard</h1>
                        <p className="text-muted-foreground mt-1">Search trends and AI quality tracking</p>
                    </div>
                    <Button onClick={fetchAnalytics} variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>

                {/* Search Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <Search className="h-4 w-4" />
                                Total Searches
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold">{stats.total_searches || 0}</p>
                            <p className="text-sm text-muted-foreground">
                                {stats.last_24_hours || 0} in last 24h
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Last 30 Days
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold">{stats.last_30_days || 0}</p>
                            <p className="text-sm text-muted-foreground">
                                {stats.unique_queries || 0} unique queries
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4" />
                                AI Generated
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold">{stats.ai_generated_total || 0}</p>
                            <p className="text-sm text-muted-foreground">
                                {stats.ai_generated_month || 0} this month
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4" />
                                Not Found
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold text-destructive">{stats.not_found_total || 0}</p>
                            <p className="text-sm text-muted-foreground">
                                {stats.not_found_month || 0} this month
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Top Searches Section */}
                <Card>
                    <CardHeader 
                        className="cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === 'searches' ? '' : 'searches')}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <BarChart3 className="h-5 w-5 text-primary" />
                                <CardTitle>Top Searched Recipes</CardTitle>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex gap-2">
                                    {['all', 'month', 'week'].map((p) => (
                                        <Button
                                            key={p}
                                            variant={period === p ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={(e) => { e.stopPropagation(); setPeriod(p); }}
                                        >
                                            {p === 'all' ? 'All Time' : p === 'month' ? '30 Days' : '7 Days'}
                                        </Button>
                                    ))}
                                </div>
                                {expandedSection === 'searches' ? (
                                    <ChevronUp className="h-5 w-5" />
                                ) : (
                                    <ChevronDown className="h-5 w-5" />
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    {expandedSection === 'searches' && (
                        <CardContent>
                            {topSearches.length === 0 ? (
                                <p className="text-muted-foreground text-center py-8">No search data yet</p>
                            ) : (
                                <div className="space-y-3">
                                    {topSearches.map((search, idx) => (
                                        <div 
                                            key={`${search.query_normalized}-${idx}`}
                                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                                        >
                                            <div className="flex items-center gap-4">
                                                <span className="text-2xl font-bold text-muted-foreground w-8">
                                                    {idx + 1}
                                                </span>
                                                <div>
                                                    <p className="font-medium">{search.query}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        Avg {search.avg_results} results • {search.sources?.join(', ') || 'unknown'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xl font-bold">{search.search_count}</p>
                                                <p className="text-xs text-muted-foreground">searches</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    )}
                </Card>

                {/* AI Quality Tracking Section */}
                <Card>
                    <CardHeader 
                        className="cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === 'quality' ? '' : 'quality')}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-primary" />
                                <CardTitle>AI Recipe Quality Tracking</CardTitle>
                            </div>
                            {expandedSection === 'quality' ? (
                                <ChevronUp className="h-5 w-5" />
                            ) : (
                                <ChevronDown className="h-5 w-5" />
                            )}
                        </div>
                        <CardDescription>
                            Track when AI-generated recipes are replaced with manual imports
                        </CardDescription>
                    </CardHeader>
                    {expandedSection === 'quality' && (
                        <CardContent className="space-y-6">
                            {/* Quality Stats */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-4 rounded-lg bg-muted/50">
                                    <p className="text-sm text-muted-foreground">Total Replacements</p>
                                    <p className="text-2xl font-bold">{improvementStats.total_replacements || 0}</p>
                                </div>
                                <div className="p-4 rounded-lg bg-muted/50">
                                    <p className="text-sm text-muted-foreground">AI → Manual</p>
                                    <p className="text-2xl font-bold">{improvementStats.ai_to_manual_count || 0}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {improvementStats.ai_replacement_rate || 0}% of replacements
                                    </p>
                                </div>
                                <div className="p-4 rounded-lg bg-muted/50">
                                    <p className="text-sm text-muted-foreground">Avg Content Improvement</p>
                                    <div className="flex items-center gap-2">
                                        <p className="text-2xl font-bold">
                                            {improvementStats.avg_content_pct_improvement > 0 ? '+' : ''}
                                            {improvementStats.avg_content_pct_improvement || 0}%
                                        </p>
                                        {improvementStats.avg_content_pct_improvement > 0 ? (
                                            <ArrowUpRight className="h-5 w-5 text-green-500" />
                                        ) : improvementStats.avg_content_pct_improvement < 0 ? (
                                            <ArrowDownRight className="h-5 w-5 text-red-500" />
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            {/* Top Replaced Countries */}
                            {qualityReport.by_country?.length > 0 && (
                                <div>
                                    <h4 className="font-medium mb-3">Most Replaced by Country</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {qualityReport.by_country.map((item) => (
                                            <span 
                                                key={item.country}
                                                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
                                            >
                                                {item.country}: {item.replacements} ({item.avg_improvement_pct > 0 ? '+' : ''}{item.avg_improvement_pct}%)
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Recent Improvements */}
                            {recentImprovements.length > 0 && (
                                <div>
                                    <h4 className="font-medium mb-3">Recent Replacements</h4>
                                    <div className="space-y-2">
                                        {recentImprovements.slice(0, 5).map((imp, idx) => (
                                            <div 
                                                key={idx}
                                                className="flex items-center justify-between p-3 rounded-lg bg-muted/30"
                                            >
                                                <div>
                                                    <p className="font-medium">{imp.recipe_name}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {imp.origin_country} • {imp.original_source} → {imp.replacement_source}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm">
                                                        {imp.quality_metrics?.content_length_pct_change > 0 ? '+' : ''}
                                                        {imp.quality_metrics?.content_length_pct_change || 0}% content
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {imp.replaced_at ? new Date(imp.replaced_at).toLocaleDateString() : 'Unknown'}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {recentImprovements.length === 0 && (
                                <p className="text-center text-muted-foreground py-8">
                                    No recipe replacements tracked yet. When you replace an AI-generated recipe 
                                    with a manual import, it will be tracked here.
                                </p>
                            )}
                        </CardContent>
                    )}
                </Card>
            </div>
        </div>
    );
}
